import { IDatePickerStrings } from "@fluentui/react";

export const i18nDatePicker: IDatePickerStrings = {
  months: [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ],

  shortMonths: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],

  days: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],

  shortDays: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],

  goToToday: "Today",
  prevMonthAriaLabel: "Previous month",
  nextMonthAriaLabel: "Next month",
  prevYearAriaLabel: "Previous year",
  nextYearAriaLabel: "Next year",
};

export const LABELS_I18N = {
  general: {
    save: "Save",
    create: "Create",
    cancel: "Cancel",
    submit: "Submit",
    close: "Close",
    delete: "Delete",
    ups: "Sorry, there was a temporary problem with the tool. Please try again later.",
  },
  error: {
    collection: "Unable to fetch collection data.",
    collectionUpdate: "Unable to update collection data.",
    collectionTemplateUpdate: "Unable to update template.",
    group: "Unable to fetch group data.",
    email: "Invalid email format",
  },
  organizationError: {
    CROSSED_OUT: "The entity has been removed from the KRS",
    NOT_FOUND: "The entity was not found in the register",
    CEIDG_NOT_FOUND: "No individual found with the given NIP or a civil partnership NIP was provided",
    CRBR_NOT_FOUND: "No Beneficial Owners found in the CRBR register for the given entity",
    REPS_NOT_FOUND: "No Representatives indicated in the KRS register for the given entity",
    AUTH: "Incorrect username or password",
    DEFAULT: "Unknown error",
    FORBIDDEN: "No active subscription. Please contact us via email in the contact menu.",
    LICENCE_FORBIDDEN: "No access to this feature. Please contact us via email in the contact menu.",
    SESSION: "Session too long, please log in again",
    DATA_INTEGRITY_ON_DATE: "No KRS entry history. Only the current entry can be downloaded.",
    NOT_FOUND_ON_DATE: "On the given date, the entity does not exist in the register or was previously removed",
    NOT_DOWNLOADED: "File was not downloaded",
  },
  organizationPage: {
    dropdown: {
      placeholder: "Select collection",
      dropdownOptions: {
        pickedHeader: "Selected collection",
        availableHeader: "Available collections",
      },
    },
    messageBar: {
      dataIntegrityConcernText: {
        part1: "We detected data issues on the KRS API side. Compare the result with the",
        part2: " official KRS entity search engine",
      },
    },
    primaryButton: {
      text: "Insert template",
    },
    downloadButton: {
      text: "Current Copy",
    },
    tabs: {
      org: "Entity",
      crbr: "Beneficiaries",
      reps: "Management Board",
    },
  },
  organizationData: {
    rowLabels: {
      krs: "KRS",
      regon: "REGON",
      nip: "NIP",
      name: "Name",
      address: "Address",
      courtOfLaw: "Register court",
      wayOfRepresentation: "Representation method",
      source: "Register",
      headquater: "Headquarters",
    },
    copied: {
      text: "Copied to clipboard",
      title: "Copy to clipboard",
      label: "Copy",
    },
    buttons: {
      downloadButtonDisabledTitle: "Downloading the current extract is not available for historical data",
      downloadButtonEnabledTitle: "Download and display the entity's current extract",
    },
  },
  CRBRData: {
    rowLabels: {
      person: "Full name",
      pesel: "PESEL",
      citizenship: "Citizenship",
    },
    copied: {
      text: "Copied to clipboard",
      title: "Copy to clipboard",
      label: "Copy",
    },
  },
  WebScraperData: {
    progressBarLabel: "Downloading list of representatives",
    disclaimerLabel: "The beta version list of representatives does not include proxies or attorneys",
    datePickerDisclaimerLabel:
      "In the beta version, the management board list is only available for the current extract",
    rowLabels: {
      person: "Full name",
      pesel: "PESEL",
      function: "Function",
    },
    copied: {
      text: "Copied to clipboard",
      title: "Copy to clipboard",
      label: "Copy",
    },
  },
  organizationSearch: {
    headerLabel: "Search for entity",
    historyCheckboxLabel: "Valid for date: ",
    toolTip: {
      date: "Searches for the last extract before the given date or the current extract if no date is selected",
      registryPicker:
        "Searches for the entity in the selected register. For KRS, enter the entity's KRS number. For CEIDG, enter the individual's NIP number.",
    },
    textField: {
      placeholder: {
        part1: "enter NIP number",
        part2: "enter KRS number",
        part3: "enter NIP, REGON or KRS number",
      },
    },
    krsErrorMessage: "Invalid number",
    datePicker: {
      ariaLabel: "Select date",
    },
    primaryButton: {
      text: "Search",
    },
  },
  organizationTutorial: {
    automatizationLabel: "Automation",
    automatizationText:
      "Information about entities registered in the KRS and individuals registered in the CEIDG is available in the blink of an eye.",
    timeLabel: "Time saving",
    timeText: "Save at least several hours of work for a price lower than the minimum hourly rate.",
    qualityLabel: "Quality",
    qualityText: "Data obtained using ",
    qualityText2: " comes directly from the KRS and CEIDG registers.",
    easeOfUseLabel: "Ease of use",
    easeOfUseText: "is a simple add-in for Microsoft Office Excel. Use it on any computer in your company.",
  },
  managementPage: {
    title: "Collection settings",
    deleteMessage: {
      default: "Deleting means you will no longer use the collection. This only affects your account",
      variant: "Complete removal of the collection from the database and from the accounts of invited collaborators",
    },
    dataGrid: {
      collectionNameHeaderCell: "Collection name",
      collectionLabelHeaderCell: "Collection description",
      collectionMemberCountHeaderCell: "Number of users",
      collectionActionsHeaderCell: "Actions",
    },
    newCollectionForm: {
      collectionNameLabel: "Collection name",
      collectionLabelLabel: "Collection description",
      maxStringLength: "Maximum number of characters is",
      collectionNameIsRequiredErrorMessage: "Collection name is required",
    },
  },
  headerComponent: {
    signInButtonLabel: "Sign in",
    signOutButtonLabel: "Sign out",
    sideMenu: {
      title: "Menu",
      aboutLabel: "About us",
      contactLabel: "Contact",
      collectionManagementLabel: "Collection settings",
    },
  },
  collectionEditComponent: {
    collectionNameLabel: "Collection name",
    collectionLabelLabel: "Collection description",
    collectionTemplateNameDropdownLabel: "Template",
    collectionTemplateTextLabel: "You are editing the template",
    collectionTemplateUnknownTag: "Unknown tag",
    dropdown: {
      groupPartnership: "Partnerships",
      groupAssociacion: "Associations",
      groupCooperative: "Cooperatives",
      groupOther: "Other",
    },
  },
  collectionInviteComponent: {
    inviteEmailLabel: "Email addresses of invited people",
  },
};

export const LEGAL_FORM_MAP = new Map();
LEGAL_FORM_MAP.set("sp_j", "GENERAL PARTNERSHIP");
LEGAL_FORM_MAP.set("sp_zoo", "LIMITED LIABILITY COMPANY");
LEGAL_FORM_MAP.set("sp_k", "LIMITED PARTNERSHIP");
LEGAL_FORM_MAP.set("sp_p", "PROFESSIONAL PARTNERSHIP");
LEGAL_FORM_MAP.set("sp_ka", "LIMITED JOINT-STOCK PARTNERSHIP");
LEGAL_FORM_MAP.set("sp_pakc", "SIMPLE JOINT-STOCK COMPANY");
LEGAL_FORM_MAP.set("sp_akc", "JOINT-STOCK COMPANY");
LEGAL_FORM_MAP.set("ezig", "EUROPEAN ECONOMIC INTEREST GROUPING");
LEGAL_FORM_MAP.set("sp_eu", "EUROPEAN COMPANY");
LEGAL_FORM_MAP.set("spol", "COOPERATIVE");
LEGAL_FORM_MAP.set("spol_eu", "EUROPEAN COOPERATIVE");
LEGAL_FORM_MAP.set("prze_pa", "STATE-OWNED ENTERPRISE");
LEGAL_FORM_MAP.set("fund", "FOUNDATION");
LEGAL_FORM_MAP.set("stow", "ASSOCIATION");
LEGAL_FORM_MAP.set("zw", "UNION OF ASSOCIATIONS");
LEGAL_FORM_MAP.set("zw_ogr", "UNION OF GARDEN ASSOCIATIONS");
LEGAL_FORM_MAP.set("stow_ogr", "GARDEN ASSOCIATION");
LEGAL_FORM_MAP.set("inol_Łu", "RESEARCH INSTITUTE");
LEGAL_FORM_MAP.set("inol_lu", "RESEARCH INSTITUTE / INSTITUTE OPERATING WITHIN THE ŁUKASIEWICZ RESEARCH NETWORK");
LEGAL_FORM_MAP.set("tow_wz", "MUTUAL INSURANCE SOCIETY");
LEGAL_FORM_MAP.set("tow_re", "MUTUAL REINSURANCE SOCIETY");
LEGAL_FORM_MAP.set("opz_rp", "BRANCH OF A FOREIGN ENTERPRISE OPERATING IN THE TERRITORY OF THE REPUBLIC OF POLAND");
LEGAL_FORM_MAP.set("goz_zu", "MAIN BRANCH OF A FOREIGN INSURANCE COMPANY");
LEGAL_FORM_MAP.set("goz_zr", "MAIN BRANCH OF A FOREIGN REINSURANCE COMPANY");
LEGAL_FORM_MAP.set("igb", "BUDGETARY ECONOMY INSTITUTION");
LEGAL_FORM_MAP.set("spzoz", "INDEPENDENT PUBLIC HEALTH CARE INSTITUTION");
LEGAL_FORM_MAP.set("kr", "AGRICULTURAL CIRCLE");
LEGAL_FORM_MAP.set("rzb", "AGRICULTURAL INDUSTRY ASSOCIATION");
LEGAL_FORM_MAP.set("zrzb", "UNION OF AGRICULTURAL INDUSTRY ASSOCIATIONS");
LEGAL_FORM_MAP.set("zrkor", "UNIONS OF FARMERS, CIRCLES AND AGRICULTURAL ORGANIZATIONS");
LEGAL_FORM_MAP.set("zzri", "TRADE UNION OF INDIVIDUAL FARMERS");
LEGAL_FORM_MAP.set("cerz", "CRAFT GUILD");
LEGAL_FORM_MAP.set("izrz", "CHAMBER OF CRAFTS");
LEGAL_FORM_MAP.set("zrp", "POLISH CRAFTS ASSOCIATION");
LEGAL_FORM_MAP.set("zhu", "TRADE AND SERVICES ASSOCIATION");
LEGAL_FORM_MAP.set("zrt", "TRANSPORT ASSOCIATION");
LEGAL_FORM_MAP.set("orzhu", "NATIONAL REPRESENTATION OF TRADE AND SERVICES ASSOCIATIONS");
LEGAL_FORM_MAP.set("orzt", "NATIONAL REPRESENTATION OF TRANSPORT ASSOCIATIONS");
LEGAL_FORM_MAP.set("iopg", "OTHER ORGANIZATION OF ECONOMIC ENTITIES");
LEGAL_FORM_MAP.set("izgo", "CHAMBER OF COMMERCE");
LEGAL_FORM_MAP.set("zwza", "TRADE UNION");
LEGAL_FORM_MAP.set("jozzpop", "ORGANIZATIONAL UNIT OF A TRADE UNION WITH LEGAL PERSONALITY");
LEGAL_FORM_MAP.set("ozwm", "NATIONAL INTER-BRANCH UNION");
LEGAL_FORM_MAP.set("ozrm", "NATIONAL INTER-BRANCH ASSOCIATION");
LEGAL_FORM_MAP.set("zwpr", "EMPLOYERS' UNION");
LEGAL_FORM_MAP.set("fkzp", "FEDERATION / CONFEDERATION OF EMPLOYERS' UNIONS");
LEGAL_FORM_MAP.set("zwsp", "SPORTS UNION");
LEGAL_FORM_MAP.set("pzwsp", "POLISH SPORTS UNION");
LEGAL_FORM_MAP.set("iosz", "OTHER SOCIAL OR PROFESSIONAL ORGANIZATION");
LEGAL_FORM_MAP.set("nopopp", "NON-GOVERNMENTAL ORGANIZATION WITHOUT LEGAL PERSONALITY");
LEGAL_FORM_MAP.set("iopopp", "OTHER LEGAL ENTITY BEING A PUBLIC BENEFIT ORGANIZATION");
LEGAL_FORM_MAP.set("kopopp", "CHURCH LEGAL ENTITY BEING A PUBLIC BENEFIT ORGANIZATION");
LEGAL_FORM_MAP.set("nopkopp", "CHURCH INSTITUTION WITHOUT LEGAL PERSONALITY BEING A PUBLIC BENEFIT ORGANIZATION");
LEGAL_FORM_MAP.set("skf", "PHYSICAL CULTURE ASSOCIATION");
LEGAL_FORM_MAP.set("inne", "BRANCH OF A FOREIGN ENTREPRENEUR");
LEGAL_FORM_MAP.set("jtspop", "LOCAL UNIT OF AN ASSOCIATION WITH LEGAL PERSONALITY");
LEGAL_FORM_MAP.set("jdg", "SOLE PROPRIETORSHIP");

export default {
  LABELS_I18N,
  LEGAL_FORM_MAP,
  i18nDatePicker,
};
